import { Button, CircularProgress, makeStyles } from '@material-ui/core';
import bbox from "@turf/bbox";
import bdistance from '@turf/distance'
import bcenter from "@turf/center";
import { lineString, point } from "@turf/helpers";
import { get as lGet, isEmpty } from "lodash";
import React, { useCallback, useEffect, useMemo } from 'react';
import { useHistory, useLocation, useRouteMatch } from 'react-router-dom';
import { atom, selector, useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import { useUserState } from 'services/authentication.service';
import { fetchState } from 'services/booking.service';
import Worker from 'src/pages/CustomerBooking/Details/clientstatus.worker.js';
import { useRunOnce, useState } from 'util/stateref';
import { get, set } from "idb-keyval";
import { analyticsIdentifierState } from '..';
import { useStateRef } from '@zennya/web-component-library/src';
import { hasOngoingBookingState } from 'pages/CustomerBookingCreate/state';
import toast from 'react-hot-toast';
import { oxfordComma } from 'util/format';

let pollWorker = new Worker();

const useStyles = makeStyles((theme) => {
  return {}
})

const autoPinCodeState = atom({
  key: 'autoPinCode',
  default: null
})

const autoPinCodeProcessingState = atom({
  key: 'autoPinCodeProcessing',
  default: false
})

const bookingState = atom({
  key: "booking",
  default: {}
})

const intendedServiceIdState = atom({
  key: "intendedServiceId",
  default: null,
})

const locationRequiredState = atom({
  key: "locationRequiredState",
  default: false
})

const isLocationRequiredPopupState = atom({
  key: "locationRequiredStatePopup",
  default: false
})
const clientState = atom({
  key: "clientState",
  default: {}
})

const clientPulseState = atom({
  key: "clientPulseState",
  default: 0
})

const mapBoundsState = atom({
  key: 'mapBounds',
  default: []
})

const centerPointState = atom({
  key: 'centerPoint',
  default: {}
})

const manualRefreshState = atom({
  key: 'manualRefresh',
  default: null
})

const highlightActionState = atom({
  key: 'highlightAction',
  default: false
})

const rebookedState = atom({
  key: 'rebookedState',
  default: false,
})

const preferredDateState = atom({
  key: 'preferredDate',
  default: null,
})

const adjustTransactionState = atom({
  key: 'adjustTransactionState',
  default: null,
})

const calculatedTransactionAmountState = atom({
  key: 'calculatedTransactionAmountState',
  default: 0,
})

const promoState = atom({
  key: 'promoState',
  default: null,
})

const newScheduleAtom = atom({
  key: 'requiresNewSchedule',
  default: false
})

const isWaitingForPollState = atom({
  key: 'isWaitingForPollState',
  default: false
})

const requiresNewScheduleState = selector({
  key: 'requiresNewScheduleState',
  get: ({ get }) => {
    const booking = get(bookingState)
    const requiresNewSchedule = get(newScheduleAtom)
    const bookingHasSchedule = booking?.requires_schedule_confirmation && booking?.is_schedule_confirmed === false
    return  bookingHasSchedule
  }
})

const adjustTransactionAsIdsState = selector({
  key: 'adjustTransactionAsIdState',
  get: ({ get }) => {
    const adjust = get(adjustTransactionState)
    if (!adjust) return {}
    return {
      extended_package_items: adjust?.extended_package_items?.filter(f => !f?.disabled)?.map(item => item?.item?.id),
      extended_packages: adjust?.extended_packages?.filter(f => !f?.disabled)?.map(item => item?.package?.id),
      packages: adjust?.packages,
    }
  },
})

const hasEditedBookingItemsState = selector({
  key: 'hasEditedBookingItems',
  get: ({ get }) => {
    const adjust = get(adjustTransactionState)
    const booking = get(bookingState)
    const adjustedExtendedPackages = adjust?.extended_package_items?.filter(f => !f?.disabled)
    const bookingExtendedPackages = booking?.extended_package_items
    const compare = adjustedExtendedPackages?.length !== bookingExtendedPackages?.length

    const adjusted = adjustedExtendedPackages?.map(item => item?.item?.id).sort()
    const bookingItems = bookingExtendedPackages?.map(item => item?.item?.id).sort()
    const compareItems = JSON.stringify(adjusted) !== JSON.stringify(bookingItems)
  
    return compareItems
    
  }
})

const modifiedBookingItemsState = selector({
  key: 'modifiedBookingItems',
  get: ({ get }) => {
    const adjust = get(adjustTransactionState)
    const adjustedExtendedPackages = adjust?.extended_package_items?.filter(f => !!f?.disabled)
    const strs = adjustedExtendedPackages?.map(item => item?.item?.label)
    const fmtStrs = oxfordComma(strs)
    return fmtStrs
  }
})



const STEP_STATE_SCHEDULE = 0
const STEP_STATE_PAYMENT = 1
const STEP_STATE_SUMMARY = 2
const STEP_STATE_LIVE = 3
const STEP_STATE_COMPLETE = 4

const stepState = atom({
  key: 'currentStep',
  default: 0
})


const medicalPinEffect = key => ({ setSelf, onSet, trigger }) => {
  const loadPersisted = async () => {
    const savedValue = JSON.parse(localStorage.getItem(key))
    if (!isEmpty(savedValue)) {
      setSelf(savedValue)
    }
  }

  if (trigger === 'get') {

    loadPersisted()
  }

  onSet((newValue, _, isReset) => {
    console.log('set: medicalpin', newValue)
    isReset ? localStorage.removeItem(key) : localStorage.setItem(key, JSON.stringify(newValue))
  })
}

const storedMedicalPinState = atom({
  key: 'storedMedicalPin',
  default: '',
  effects: [
    medicalPinEffect('storedMedicalPin')
  ]
})
const medicalPinState = atom({
  key: 'medicalPin',
  default: null,
})

const RecoilState = (props) => {
  const { serviceId, children } = props;
  const classes = useStyles()
  const history = useHistory()
  const isRouteMap = useRouteMatch({
    path: '/bookings/:serviceId/map', strict: true,
    sensitive: true
  })

  const isRouteRating = useRouteMatch({
    path: '/bookings/:serviceId/rating', strict: true,
    sensitive: true
  })

  const isRouteProfile = useRouteMatch({
    path: '/bookings/:serviceId/providerProfile', strict: true,
    sensitive: true
  })

  const isRouteCode = useRouteMatch({
    path: [
      '/bookings/:serviceId/code/:code',
    ],
    strict: true,
    sensitive: true
  })
  const location = useLocation()

  const [loading, setLoading] = useState(true);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [isInvalid, setIsInvalid] = useState();
  const { user, getUser, segmentLoginAnonymous, fetchBookingByAuth } = useUserState()
  const [userState, setUserState, userStateRef] = useState();

  const [isOnGoing, setIsOnGoing] = useState(false);

  const setClientPulseState = useSetRecoilState(clientPulseState)
  const setClientData = useSetRecoilState(clientState);
  const setBooking = useSetRecoilState(bookingState);
  const setBounds = useSetRecoilState(mapBoundsState);
  const setCenterPoint = useSetRecoilState(centerPointState);
  const setLocationRequiredState = useSetRecoilState(locationRequiredState);
  const setIsLocationRequiredPopupState = useSetRecoilState(isLocationRequiredPopupState);
  const [adjustTransaction, setAdjustTransaction]= useRecoilState(adjustTransactionState);

  const intendedServiceId = useRecoilValue(intendedServiceIdState)
  const setIntendedServiceId = useSetRecoilState(intendedServiceIdState);

  const manualRefresh = useRecoilValue(manualRefreshState);
  const setManualRefreshState = useSetRecoilState(manualRefreshState);

  const [preferredDate, setPreferredDate] = useRecoilState(preferredDateState);

  const [hasOngoingBooking, setHasOngoingBooking] = useRecoilState(hasOngoingBookingState);

  const [isWaitingForPoll, setIsWaitingForPoll]= useRecoilState(isWaitingForPollState);
  
  const optToastRedirect = useCallback(
    (clientStatus, route) => {
      if (route) {
        toast(t => {
          return <Button
            variant='contained'
            color='secondary'
            onClick={() => {
              toast.dismiss(t.id)
              history.push(`/bookings/${clientStatus?.appointment?.request?.id}/${route}` + history.location.search)
            }}>
            You have an ongoing booking. Click here to view.
          </Button>
        }, {
          duration: Infinity,
          id: 'ongoing-booking',
          style: {
            backgroundColor: 'transparent',
            boxShadow: 'none',
          }
        })
      }
    },
    [hasOngoingBooking],
  );
  const mapToState = useCallback(async (clientStatus, bookingData) => {
    setBooking(bookingData)

    if (!clientStatus.appointment) {
    } else {
      const provider = {
        latitude: lGet(clientStatus, "appointment.provider_lat"),
        longitude: lGet(clientStatus, "appointment.provider_lng"),
        type: "provider",
        eta: lGet(clientStatus, "appointment.provider_eta"),
        travelMethod: lGet(clientStatus, "appointment.travel_method", 'walking'),
        provider: {
          profilePicUrl: lGet(
            clientStatus,
            "appointment.provider.profile_pic_url"
          ),
          type: lGet(clientStatus, "appointment.provider.provider_type"),
          gender: lGet(clientStatus, "appointment.provider.gender"),
        },
        state: lGet(clientStatus, "state")
      };
      const original = {
        latitude: lGet(clientStatus, "appointment.original_lat"),
        longitude: lGet(clientStatus, "appointment.original_lng"),
        type: "original",
        eta: lGet(clientStatus, "appointment.original_eta"),
      };
      const request = {
        latitude: lGet(clientStatus, "appointment.request.latitude"),
        longitude: lGet(clientStatus, "appointment.request.longitude"),
        type: "request",
      };
      const userLocation = {
        latitude: lGet(clientStatus, "appointment.request.user_location.latitude"),
        longitude: lGet(
          clientStatus,
          "appointment.request.user_location.longitude"
        ),
        location: lGet(
          clientStatus,
          "appointment.request.user_location.type.name"
        ),
        eta: lGet(clientStatus, "appointment.provider_eta"),
        type: "userLocation",
      };

      const markers = [provider, userLocation];

      const distance = bdistance(point([userLocation.longitude, userLocation.latitude]), point([provider.longitude, provider.latitude]))

      provider.distance = distance

      const boundLine = lineString(
        markers.map((marker) => [marker.latitude, marker.longitude])
      );
      const boundingBox = bbox(boundLine);
      const centerPoint = bcenter(boundLine)

      if (boundingBox) {
        const [minLat, minLng, maxLat, maxLng] = boundingBox;
        setBounds(
          [
            [minLng, minLat],
            [maxLng, maxLat]
          ]
        );
        setCenterPoint(centerPoint);
      }
      setClientData({ markers, centerPoint, boundingBox, ...clientStatus })
      setClientPulseState(new Date())
    }

    switch (clientStatus?.state) {
      case 'busy':
        setIntendedServiceId(serviceId)
        if (serviceId === clientStatus?.appointment?.request?.id) {
          history.replace(`/bookings/${clientStatus?.appointment?.request?.id}/ongoing` + history.location.search)
        } else {
          if (clientStatus?.appointment?.request?.id) {
            setHasOngoingBooking('ongoing')
            optToastRedirect(clientStatus, 'ongoing')
          }
        }
        return
      case 'rating':
        if (isEmpty(isRouteRating)) {
          setIntendedServiceId(serviceId)
          if (serviceId === clientStatus?.appointment?.request?.id) {
            history.replace(`/bookings/${clientStatus?.appointment?.request?.id}/rating` + history.location.search)
          } else {
            if (clientStatus?.appointment?.request?.id) {
              setHasOngoingBooking('rating')
              optToastRedirect(clientStatus, 'rating')
            }
          }
        }
        return

      default:
        break;
    }


    if (isRouteProfile) {

    } else {
      if (serviceId === clientStatus?.appointment?.request?.id) {
        if (location?.state?.from !== 'map') {
          // if (clientStatus?.appointment?.preparing === false && !isEmpty(clientStatus?.appointment?.date_preparing_done)) {
          if (isEmpty(isRouteMap)) {
            history.replace(`/bookings/${serviceId}/map` + history.location.search)
          }
          return
          // }
        }
        setHasOngoingBooking(null)
        optToastRedirect(clientStatus, null)
      } else {
        // if (clientStatus?.appointment?.request?.id) {
        //   history.replace(`/bookings/${clientStatus?.appointment?.request?.id}/map` + history.location.search)
        // }
        if (clientStatus?.appointment?.request?.id) {
          setHasOngoingBooking('map')
          optToastRedirect(clientStatus, 'map')
        }
      }

      if (clientStatus?.state === 'busy') {
        setIsOnGoing(true)
      }
    }
    // if (isEmpty(clientStatus?.appointment)) {
    //   history.replace(`/bookings/${serviceId}` + history.location.search)
    // }

  }, [history, isRouteMap, isRouteProfile, isRouteRating, location?.state?.from, serviceId, setBooking, setBounds, setCenterPoint, setClientData, setClientPulseState])

  const [analyticsIdentifier, setAnalyticsIdentifier] = useRecoilState(analyticsIdentifierState);
  const fetchBooking = useCallback(
    async (svcId) => {
      if (isRouteCode) {
        setLoading(false)
        return
      }
      setLoading(true)

      let result
      try {
        result = await fetchBookingByAuth(svcId, analyticsIdentifier)
        setAnalyticsIdentifier(result?.client?.id)
        if (result?.date_scheduled) {
          setBooking(result)
        }

        setLocationRequiredState(result?.is_whitelabel && result?.requires_location_confirmation)
        setIsLocationRequiredPopupState(result?.is_whitelabel && result?.requires_location_confirmation)
      } catch (e) {
        console.log("📢[index.jsx:294]: ", e);
        setLoading(false)
        history.replace(`/bookings/oops/${svcId}` + history.location.search)
        return
      }
      if (result?.request_code || result?.requires_validation) {
        setLoading(false)
        history.replace(`/bookings/login/${svcId}` + history.location.search)
        return
      }

      const cUser = await getUser()
      if (cUser) {
        if ((cUser?.client?.id || cUser?.user?.id) !== result?.client?.id) {
          setIsInvalid(true)
          setLoading(false)
          history.replace(`/bookings/oops/${svcId}` + history.location.search)
          return;
        }
      }

      if (!cUser) {
        setLoading(false)
        console.log('index.jsx (306) # userStateRef?.current', userStateRef?.current);
        // history.replace(`/bookings/login/${svcId}` + history.location.search)
        return
      }



      try {
        const resClientState = await fetchState(result?.client?.id)
        await mapToState(resClientState, result)
        setAdjustTransaction({
          extended_package_items: result?.extended_package_items,
          extended_packages: result?.extended_packages,
          packages: result?.packages,
        })
        setLocationRequiredState(result?.is_whitelabel && result?.requires_location_confirmation)
        setIsLocationRequiredPopupState(result?.is_whitelabel && result?.requires_location_confirmation)
        setLoading(false)

        setIsInvalid(false)
        setDataLoaded(true)
        setPreferredDate(null)
      } catch (e) {
        setLoading(false)
        history.replace(`/bookings/oops/${svcId}` + history.location.search)
        return
      }

    },
    [history, mapToState, userStateRef?.current],
  )

  const verifyInAppCode = useCallback(
    (code) => {
      history.push(`/bookings/${serviceId}/code/${code}` + history.location.search)
    },
    [history, serviceId],
  );

  const fetchUser = useCallback(
    async () => {
      const user = await getUser(serviceId, true)
      if (!user) {
        if (!isRouteCode) {
          history.replace(`/bookings/login/${serviceId}` + history.location.search)
        }
        // await segmentLoginAnonymous()
      }
      setUserState(user)
      return user
    },
    [],
  );

  useEffect(() => {
    if (serviceId) {
      fetchUser()
    }
  }, [serviceId]);


  const [svcR, setSvcR, svcRef] = useStateRef();
  useEffect(() => {
    async function fb() {
      if (!svcRef?.current) {
        setSvcR(serviceId)
        await fetchBooking(serviceId)
      }
    }

    fb()

  }, []);

  useEffect(
    () => {
      async function postmsg() {
        let usr = user
        if (!(usr?.token && (user?.client?.id || user?.user?.id))) {
          usr = await fetchUser()
        }
        if (pollWorker && usr?.token && (user?.client?.id || user?.user?.id)) {
          pollWorker.postMessage({ codeId: serviceId, token: usr?.token, clientId: (user?.client?.id || user?.user?.id) })
        }
      }
      if (manualRefresh) {
        postmsg()
      }
    }, [manualRefresh]
  );

  useEffect(() => {
    if (!pollWorker) pollWorker = new Worker();
    return () => {
      console.log('UEF:onterminate');
      pollWorker?.terminate();
      pollWorker = null
    };
  }, []);

  useEffect(() => {
    return () => {
      if (isInvalid === true) {
        console.log('UEF:onterminatevalid');
        pollWorker?.terminate();
        pollWorker = null
      }
    };
  }, [isInvalid]);

  useEffect(() => {
    if (pollWorker && user?.token && (user?.client?.id || user?.user?.id)) {
      pollWorker.postMessage({ codeId: serviceId, token: user?.token, clientId: (user?.client?.id || user?.user?.id) })
    }
  }, [serviceId, user?.token, user?.client?.id, user?.user?.id]);


  const processPoll = useCallback(
    async (message) => {
      const clientStatus = message?.data?.clientStatus
      const result = message?.data?.bookingResult
      await mapToState(clientStatus, result)
      if (!adjustTransaction) {      
        setAdjustTransaction({
          extended_package_items: result?.extended_package_items,
          extended_packages: result?.extended_packages,
          packages: result?.packages,
        })
      }
      setLocationRequiredState(result?.requires_location_confirmation)
      setIsLocationRequiredPopupState(result?.requires_location_confirmation)
      setLoading(false)
      setLocationRequiredState(
        result?.is_whitelabel &&
        result?.requires_location_confirmation
      )
      setIsLocationRequiredPopupState(
        result?.is_whitelabel &&
        result?.requires_location_confirmation
      )
      setDataLoaded(true)
      setIsWaitingForPoll(false)

    },
    [mapToState, adjustTransaction],
  );

  useEffect(() => {
    pollWorker.onmessage = message => {
      processPoll(message)
    }
  }, [processPoll]);


  if (loading) {
    return <CircularProgress />
  } else {
    return (
      <>
        {children}
      </>

    )
  }
}

export {
  STEP_STATE_SCHEDULE,
  STEP_STATE_PAYMENT,
  STEP_STATE_SUMMARY,
  STEP_STATE_LIVE,
  STEP_STATE_COMPLETE,
  medicalPinState,
  storedMedicalPinState,
  stepState,
  clientPulseState,
  locationRequiredState,
  isLocationRequiredPopupState,
  RecoilState, manualRefreshState, bookingState, clientState, mapBoundsState, centerPointState,
  rebookedState,
  autoPinCodeState,
  autoPinCodeProcessingState,
  highlightActionState,
  preferredDateState,
  intendedServiceIdState,
  adjustTransactionState,
  adjustTransactionAsIdsState,
  hasEditedBookingItemsState,
  calculatedTransactionAmountState,
  promoState,
  newScheduleAtom,
  requiresNewScheduleState,
  isWaitingForPollState,
  modifiedBookingItemsState
};
